import { useCallback, useEffect, useState } from 'react'
import { adminProcessingApi, type ProcessingState } from '@/services/api/adminProcessing'

function errMessage(err: unknown, fallback: string): string {
  const e = err as { message?: string }
  return e?.message ?? fallback
}

/** Live processing state for a time range. Fetches `adminProcessingApi.state`
 *  immediately and re-polls on `refreshMs`. Returns the latest state plus
 *  loading / error flags for the page to render. */
export function useProcessingState(rangeHours: number, refreshMs: number) {
  const [state, setState] = useState<ProcessingState | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      const s = await adminProcessingApi.state(rangeHours)
      setState(s)
      setError(null)
    } catch (err) {
      setError(errMessage(err, 'Failed to load'))
    } finally {
      setLoading(false)
    }
  }, [rangeHours])

  useEffect(() => {
    refresh()
    const id = setInterval(refresh, refreshMs)
    return () => clearInterval(id)
  }, [refresh, refreshMs])

  return { state, loading, error }
}
